import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Music, Wine, UtensilsCrossed } from 'lucide-react';
import { IMAGES, SITE } from '../data/content';
import { TileStrip } from '../components/TileStrip';
import { InstagramSection } from '../components/InstagramSection';
import { Reveal } from '../components/Reveal';
import { SerapeUnderline } from '../components/Serape';

const HomePage = () => {
  return (
    <div data-testid="home-page" className="bg-brand-night">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <img src={IMAGES.heroCantarito} alt="Cantarito cocktail" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-night via-brand-night/55 to-brand-night/35" />

        <div className="relative mx-auto max-w-7xl w-full px-6 md:px-12 pb-20 md:pb-28 text-brand-cream">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="uppercase text-sm tracking-[0.35em] font-semibold text-brand-gold mb-5"
          >
            Mezcalería · Cocina de México · Dallas, TX
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-black text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] text-white"
          >
            CANTARITOS<br />DALLAS
          </motion.h1>
          <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ duration: 0.7, delay: 0.3 }} style={{ transformOrigin: 'left' }}>
            <SerapeUnderline className="mt-6" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-6 max-w-xl text-lg text-white/85"
          >
            Nacido de las calles de México — cócteles en jarrito de barro sobre cítricos frescos, una gran selección de agaves y bandas mexicanas en vivo que convierten la cena en una fiesta.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link to="/menu" data-testid="hero-menu-cta" className="group inline-flex items-center gap-2 bg-brand-terracotta text-white px-7 py-4 text-sm uppercase tracking-widest font-medium hover:bg-brand-terracottaDark transition-colors">
              Ver Menú <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/live-music" data-testid="hero-music-cta" className="inline-flex items-center gap-2 border border-brand-cream/40 text-brand-cream px-7 py-4 text-sm uppercase tracking-widest font-medium hover:bg-brand-gold hover:text-brand-night hover:border-brand-gold transition-colors">
              Música en Vivo
            </Link>
          </motion.div>
        </div>
      </section>

      <TileStrip />

      {/* INTRO / CONCEPT */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <Reveal>
            <p className="uppercase text-sm tracking-[0.3em] font-semibold text-brand-gold mb-5">El Concepto</p>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight leading-tight text-brand-cream">
              La buena vida, servida en jarrito de barro.
            </h2>
            <SerapeUnderline className="mt-6" />
            <p className="mt-6 text-brand-cream/70 leading-relaxed">
              En México, el <em>cantarito</em> es más que una bebida — es un ritual. El tequila y el mezcal se encuentran con limón, naranja y toronja en un jarrito de barro hecho a mano que mantiene cada sorbo fresco y terroso. Trajimos ese ritual a Dallas, lo acompañamos de cocina callejera intensa y le pusimos de fondo bandas mexicanas en vivo.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[{ n: '40+', l: 'Destilados de Agave' }, { n: '5', l: 'Cantaritos de la Casa' }, { n: 'En Vivo', l: 'Bandas por Semana' }].map((s) => (
                <div key={s.l}>
                  <p className="font-display font-black text-3xl sm:text-4xl text-brand-gold">{s.n}</p>
                  <p className="text-xs uppercase tracking-widest text-brand-muted mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="relative">
            <img src={IMAGES.cantarito2} alt="Cantarito close up" className="w-full h-[420px] lg:h-[520px] object-cover" />
            <img src={IMAGES.cantarito3} alt="Cantarito detail" className="hidden sm:block absolute -bottom-10 -left-10 w-44 h-56 object-cover border-8 border-brand-night shadow-2xl" />
          </Reveal>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="relative bg-brand-coal overflow-hidden">
        <div className="tile-greca h-9 w-full opacity-50" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight max-w-2xl text-brand-cream">
              Come. Bebe. Baila.
            </h2>
            <SerapeUnderline className="mt-5" />
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: Wine, t: 'Cantaritos y Mezcal', d: 'Clásicos en jarrito de barro y una gran selección de agaves inspirados en los sabores de México.', to: '/menu', img: IMAGES.cantarito2 },
              { icon: UtensilsCrossed, t: 'Cocina de México', d: 'Tacos, quesatacos, molcajetes y más — hechos para compartir.', to: '/menu', img: IMAGES.tacos1 },
              { icon: Music, t: 'Bandas Mexicanas en Vivo', d: 'Mariachi, banda y norteño varias noches a la semana. Ven por la música, quédate por el brindis.', to: '/live-music', img: IMAGES.mariachi },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.1}>
                <Link to={c.to} data-testid={`pillar-card-${i}`} className="group block bg-brand-night border border-brand-line overflow-hidden h-full hover:border-brand-gold transition-colors">
                  <div className="h-48 overflow-hidden">
                    <img src={c.img} alt={c.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-8">
                    <c.icon className="text-brand-gold mb-4" size={26} />
                    <h3 className="font-display font-semibold text-2xl tracking-tight text-brand-cream">{c.t}</h3>
                    <p className="mt-3 text-brand-cream/65 text-sm leading-relaxed">{c.d}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-brand-gold">
                      Explorar <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVATE EVENTS BANNER */}
      <section className="relative overflow-hidden">
        <img src={IMAGES.interior} alt="Restaurant interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-night/80" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 py-28 md:py-36 text-brand-cream">
          <Reveal>
            <p className="uppercase text-sm tracking-[0.3em] font-semibold text-brand-gold mb-5">Eventos Privados</p>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight max-w-3xl leading-tight">
              Celebra donde corre el agave y suena la banda.
            </h2>
            <SerapeUnderline className="mt-6" />
            <p className="mt-6 max-w-2xl text-brand-cream/80">
              Cumpleaños, eventos corporativos, quinceañeras y bodas — creamos una experiencia de cantaritos a la medida de tus invitados.
            </p>
            <Link to="/private-events" data-testid="home-events-cta" className="mt-8 inline-flex items-center gap-2 bg-brand-gold text-brand-night px-7 py-4 text-sm uppercase tracking-widest font-semibold hover:bg-brand-citrus transition-colors">
              Planea tu Evento <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INSTAGRAM */}
      <InstagramSection />

      {/* VISIT CTA */}
      <section className="relative bg-brand-coal overflow-hidden">
        <div className="pattern-dots-warm absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 py-24 md:py-32 text-center">
          <Reveal>
            <p className="uppercase text-sm tracking-[0.3em] font-semibold text-brand-gold mb-5">¡Salud!</p>
            <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter max-w-3xl mx-auto leading-none text-brand-cream">
              Encuéntranos en el corazón de Dallas.
            </h2>
            <p className="mt-6 text-brand-cream/70">{SITE.address}</p>
            <Link to="/contact" data-testid="home-contact-cta" className="mt-8 inline-flex items-center gap-2 border border-brand-gold text-brand-gold px-7 py-4 text-sm uppercase tracking-widest font-medium hover:bg-brand-gold hover:text-brand-night transition-colors">
              Contáctanos
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
